import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import ClaimSuccessModal from "./SupportSuccessModal";
import { dateToUnixTimestamp } from "../../../../util_functions/helpers";
import axios from "axios";
import config from "../../../../config/config";
import moment from "moment";

const SupportClaimForm = () => {
  const { workerUser } = useOutletContext();
  const [agreements, setAgreements] = useState([]);
  const [previousClaims, setPreviousClaims] = useState([]);
  const [selectedAgreementId, setSelectedAgreementId] = useState("");
  const [formData, setFormData] = useState({
    userId: workerUser.id,
    paymentAccount: workerUser.paymentDetail.accountId,
    agreementId: "",
    startDate: { day: "", month: "", year: "" },
    startTime: { hour: "", minute: "", period: "AM" },
    endDate: { day: "", month: "", year: "" },
    endTime: { hour: "", minute: "", period: "PM" },
    rate: { type: "", value: "" },
    notes: "",
    clientId: "",
    clientName: "",
    workerName: "",
    initialGreetingSessionRate: "",
    status: "Pending",
    invoiceSent: false,
    invoicePaid: false,
    isAllowedEdit: true,
    claimSubmissionDate: new Date().toISOString(),
    claimApprovalDate: null,
    isEdited: false,
    editCount: 0,
    isFinalized: false,
    finalizedDate: null,
    lastEditedDate: null,
  });
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/v1/agreements/user/${workerUser.userType}/${workerUser.id}`
        );
        const signedAgreements = response.data.filter(
          (agreement) => agreement.status === "FULLY_SIGNED"
        );
        setAgreements(signedAgreements);
      } catch (error) {
        console.error("Error fetching agreements:", error);
      }
    };

    const fetchPreviousClaims = async () => {
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/v1/claims/user/${workerUser.id}`
        );
        setPreviousClaims(response.data);
      } catch (error) {
        console.error("Error fetching previous claims:", error);
      }
    };

    if (workerUser.id) {
      fetchAgreements();
      fetchPreviousClaims();
    }
  }, [workerUser.id, workerUser.userType]);

  useEffect(() => {
    const selectedAgreement = agreements.find(
      (agreement) => agreement.agreementId === selectedAgreementId
    );

    if (selectedAgreement) {
      console.log("Selected Agreement:", selectedAgreement);
      const ratesArray = Object.keys(selectedAgreement.rates || {})
        .filter(
          (rateType) =>
            selectedAgreement.rates[rateType] !== null &&
            selectedAgreement.rates[rateType] !== ""
        )
        .map((rateType) => ({
          type: rateType,
          value: selectedAgreement.rates[rateType],
        }));

      setFormData((prev) => ({
        ...prev,
        userId: workerUser.id,
        paymentAccount: workerUser.paymentDetail.accountId,
        agreementId: selectedAgreementId,
        notes: "",
        clientId: selectedAgreement.clientId,
        clientName: selectedAgreement.clientName,
        workerName: selectedAgreement.workerName,
        initialGreetingSessionRate: selectedAgreement.initialGreetingSessionRate
          ? (selectedAgreement.initialGreetingSessionRate * 0.93).toFixed(2)
          : "",
        rate:
          ratesArray.length > 0
            ? {
                type: ratesArray[0].type,
                value: (ratesArray[0].value * 0.93).toFixed(2),
              }
            : { type: "", value: "" },
        startDate: {
          day: selectedAgreement.startDate?.day.toString() || "",
          month: selectedAgreement.startDate?.month.toString() || "",
          year: selectedAgreement.startDate?.year.toString() || "",
        },
        startTime: {
          hour: selectedAgreement.startTime?.hour.toString() || "",
          minute: selectedAgreement.startTime?.minute.toString() || "",
          period: selectedAgreement.startTime?.period || "AM",
        },
        endDate: {
          day: selectedAgreement.endDate?.day?.toString() || "",
          month: selectedAgreement.endDate?.month?.toString() || "",
          year: selectedAgreement.endDate?.year?.toString() || "",
        },
        endTime: {
          hour: selectedAgreement.endTime?.hour.toString() || "",
          minute: selectedAgreement.endTime?.minute.toString() || "",
          period: selectedAgreement.endTime?.period || "PM",
        },
      }));
    } else {
      setFormData({
        userId: workerUser.id,
        paymentAccount: workerUser.paymentDetail.accountId,
        agreementId: "",
        notes: "",
        clientName: "",
        workerName: "",
        initialGreetingSessionRate: "",
        rate: { type: "", value: "" },
        startDate: { day: "", month: "", year: "" },
        startTime: { hour: "", minute: "", period: "AM" },
        endDate: { day: "", month: "", year: "" },
        endTime: { hour: "", minute: "", period: "PM" },
        status: "Pending",
        invoiceSent: false,
        invoicePaid: false,
        isAllowedEdit: true,
        claimSubmissionDate: new Date().toISOString(),
        claimApprovalDate: null,
        isEdited: false,
        editCount: 0,
        isFinalized: false,
        finalizedDate: null,
        lastEditedDate: null,
      });
    }
  }, [selectedAgreementId, agreements]);

  const handleSelectChange = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleRateTypeChange = (type) => {
    const rate = agreements.find((ag) => ag.agreementId === selectedAgreementId)
      ?.rates[type];
    if (rate) {
      const newRateValue = (rate * 0.93).toFixed(2);
      setFormData((prev) => ({
        ...prev,
        rate: {
          type: type,
          value: newRateValue,
        },
      }));
    }
  };

  const formatDate = (year, month, day) => {
    return `${year}-${month.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;
  };

  function formatTime(hour, minute, period) {
    return `${hour}:${minute} ${period}`;
  }

  const getLocalDateTimeISO = (date) => {
    return moment(date).format("YYYY-MM-DDTHH:mm:ss");
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = [2023, 2024, 2025];
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const periods = ["AM", "PM"];

  const handleTimeChange = (field, value) => {
    if (value.length <= 2 && /^[0-9]*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          minute: value,
        },
      }));
    }
  };

  const handleNoteChange = (event) => {
    const newNotes = event.target.value;
    setFormData((prevFormData) => ({
      ...prevFormData,
      notes: newNotes,
    }));
  };

  const handleSubmitClaim = async () => {
    const currentSubmissionDate = new Date();
    const initialGreetingRate = isInitialGreetingRateSubmitted()
      ? "0.00"
      : formData.initialGreetingSessionRate;

    const dataToSubmit = {
      ...formData,
      startDate: dateToUnixTimestamp(
        formatDate(
          formData.startDate.year,
          formData.startDate.month,
          formData.startDate.day
        )
      ),
      endDate: dateToUnixTimestamp(
        formatDate(
          formData.endDate.year,
          formData.endDate.month,
          formData.endDate.day
        )
      ),
      startTime: formatTime(
        formData.startTime.hour,
        formData.startTime.minute,
        formData.startTime.period
      ),
      endTime: formatTime(
        formData.endTime.hour,
        formData.endTime.minute,
        formData.endTime.period
      ),
      rates: [
        {
          type: formData.rate.type,
          value: parseFloat(formData.rate.value),
        },
      ],
      claimId: `OSHCL_${Date.now().toString()}`,
      clientId: formData.clientId,
      clientName: formData.clientName,
      workerName: formData.workerName,
      status: formData.status,
      invoiceSent: formData.invoiceSent,
      invoiceSentDate: formData.invoiceSent ? Date.now() : null,
      invoicePaid: formData.invoicePaid,
      invoicePaidDate: formData.invoicePaid ? Date.now() : null,
      isAllowedEdit: formData.isAllowedEdit,
      claimSubmissionDate: Date.now(),
      claimApprovalDate: formData.claimApprovalDate
        ? dateToUnixTimestamp(formData.claimApprovalDate)
        : null,
      isEdited: formData.isEdited,
      editCount: formData.editCount,
      isFinalized: formData.isFinalized,
      finalizedDate: formData.finalizedDate
        ? dateToUnixTimestamp(formData.finalizedDate)
        : null,
      lastEditedDate: formData.lastEditedDate
        ? dateToUnixTimestamp(formData.lastEditedDate)
        : null,
      notes: formData.notes,
      initialGreetingSessionRate: initialGreetingRate,
    };

    console.log("Submitting form data:", dataToSubmit);
    // try {
    //   const response = await axios.post(
    //     `${config.API_BASE_URL}/v1/claims`,
    //     dataToSubmit
    //   );
    //   console.log("Claim Submitted:", response.data);
    //   setShowModal(true);
    // } catch (error) {
    //   console.error("Error submitting claim:", error);
    // }
  };

  const isInitialGreetingRateSubmitted = () => {
    return previousClaims.some(
      (claim) =>
        claim.agreementId === selectedAgreementId &&
        claim.initialGreetingSessionRate !== "0.00"
    );
  };

  return (
    <div className="supportclaim-form-container">
      {showModal && <ClaimSuccessModal onClose={() => setShowModal(false)} />}
      <div className="supportclaim-form-content">
        <h2>Support Work TimeSheet</h2>
        <label>
          Select your agreement below{" "}
          <span style={{ color: "green" }}>&#x25BC;</span>
        </label>
        <select
          value={selectedAgreementId}
          onChange={(e) => setSelectedAgreementId(e.target.value)}
        >
          <option value=""> </option>
          {agreements.map((agreement) => (
            <option key={agreement.agreementId} value={agreement.agreementId}>
              {agreement.clientName} - {agreement.jobTitle}
            </option>
          ))}
        </select>
        <h3>Session Start date</h3>
        <div className="horizontal-selects">
          <label>
            Day
            <select
              value={formData.startDate.day}
              onChange={(e) =>
                handleSelectChange("startDate", "day", e.target.value)
              }
            >
              <option value=""></option>
              {days.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label>
            Month
            <select
              value={formData.startDate.month}
              onChange={(e) =>
                handleSelectChange("startDate", "month", e.target.value)
              }
            >
              <option value=""></option>
              {months.map((month, index) => (
                <option key={month} value={index + 1}>
                  {month}
                </option>
              ))}
            </select>
          </label>
          <label>
            Year
            <select
              value={formData.startDate.year}
              onChange={(e) =>
                handleSelectChange("startDate", "year", e.target.value)
              }
            >
              <option value=""></option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
        <h3>Start time</h3>
        <div className="horizontal-selects">
          <label>
            Hour
            <select
              value={formData.startTime.hour}
              onChange={(e) =>
                handleSelectChange("startTime", "hour", e.target.value)
              }
            >
              <option value=""></option>
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
          </label>
          <label>
            Minute
            <input
              type="number"
              value={formData.startTime.minute}
              onChange={(e) => handleTimeChange("startTime", e.target.value)}
              min="0"
              max="59"
              placeholder="MM"
            />
          </label>
          <label>
            AM/PM
            <select
              value={formData.startTime.period}
              onChange={(e) =>
                handleSelectChange("startTime", "period", e.target.value)
              }
            >
              {periods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </label>
        </div>
        <h3>Session End date</h3>
        <div className="horizontal-selects">
          <label>
            Day
            <select
              value={formData.endDate.day}
              onChange={(e) =>
                handleSelectChange("endDate", "day", e.target.value)
              }
            >
              <option value=""></option>
              {days.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label>
            Month
            <select
              value={formData.endDate.month}
              onChange={(e) =>
                handleSelectChange("endDate", "month", e.target.value)
              }
            >
              <option value=""></option>
              {months.map((month, index) => (
                <option key={month} value={index + 1}>
                  {month}
                </option>
              ))}
            </select>
          </label>
          <label>
            Year
            <select
              value={formData.endDate.year}
              onChange={(e) =>
                handleSelectChange("endDate", "year", e.target.value)
              }
            >
              <option value=""></option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
        <h3>End time</h3>
        <div className="horizontal-selects">
          <label>
            Hour
            <select
              value={formData.endTime.hour}
              onChange={(e) =>
                handleSelectChange("endTime", "hour", e.target.value)
              }
            >
              <option value=""></option>
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
          </label>
          <label>
            Minute
            <input
              type="number"
              value={formData.endTime.minute}
              onChange={(e) => handleTimeChange("endTime", e.target.value)}
              min="0"
              max="59"
              placeholder="MM"
            />
          </label>
          <label>
            AM/PM
            <select
              value={formData.endTime.period}
              onChange={(e) =>
                handleSelectChange("endTime", "period", e.target.value)
              }
            >
              {periods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </label>
        </div>
        {formData.rate.type && (
          <div>
            <h3>Agreed Rates</h3>
            <div className="rates-container">
              <div className="rate-detail">
                <label>Schedule Type</label>
                <select
                  value={formData.rate.type}
                  onChange={(e) => handleRateTypeChange(e.target.value)}
                >
                  {agreements.find(
                    (ag) => ag.agreementId === selectedAgreementId
                  )?.rates &&
                    Object.keys(
                      agreements.find(
                        (ag) => ag.agreementId === selectedAgreementId
                      ).rates
                    ).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                </select>
              </div>
              <div className="rate-detail">
                <label>Rate ($)</label>
                <input
                  type="text"
                  value={formData.rate.value}
                  readOnly
                  placeholder="Rate Value"
                />
              </div>
            </div>
          </div>
        )}
        {formData.initialGreetingSessionRate &&
          formData.initialGreetingSessionRate !== "0.00" &&
          !isInitialGreetingRateSubmitted() && (
            <div>
              <div className="rate-detail">
                <label>Initial Greeting Session Rate ($)</label>
                <input
                  type="text"
                  value={formData.initialGreetingSessionRate}
                  readOnly
                  placeholder="Initial Greeting Session Rate"
                />
              </div>
            </div>
          )}

        <h3>Support notes</h3>
        <textarea value={formData.notes} onChange={handleNoteChange}></textarea>
        <p>
          <strong>Note:</strong> Please submit separate claims for day and night
          shifts, etc.. if applicable.
        </p>
        <button
          className="supportclaim-submit-button"
          onClick={handleSubmitClaim}
          disabled={!selectedAgreementId}
        >
          Submit claim
        </button>
      </div>
    </div>
  );
};

export default SupportClaimForm;
